// SocialPulse — shared Firebase project (voltaic-space-843)
// This module initializes the Firebase Web SDK once and exposes helpers for
// the three sign-in methods NaturePulse supports: Google, Email Link, Phone.
//
// CRITICAL: Do NOT call `signInWithEmailLink` on page-load automatically.
// Gmail (and other providers) run URL scanners that pre-fetch links inside
// emails; auto-consuming the link burns the one-time OOB code before the
// user actually clicks. Instead, we render a "Confirm Email" button and
// only call `signInWithEmailLink` after an explicit user click.

import { initializeApp, getApps, getApp } from "firebase/app";
import {
  getAuth,
  browserLocalPersistence,
  setPersistence,
  GoogleAuthProvider,
  signInWithPopup,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  onIdTokenChanged,
  signOut as fbSignOut,
} from "firebase/auth";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

export const firebaseApp = getApps().length ? getApp() : initializeApp(firebaseConfig);
export const auth = getAuth(firebaseApp);
// Persist across reloads and tabs
setPersistence(auth, browserLocalPersistence).catch(() => {});

// ---- Google popup ----
export async function signInWithGoogle() {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  const result = await signInWithPopup(auth, provider);
  return result.user;
}

// ---- Email link (passwordless) ----
const EMAIL_KEY = "sp_emailForSignIn";

export async function requestEmailLink(email) {
  const url = `${window.location.origin}/finish-signin`;
  await sendSignInLinkToEmail(auth, email, {
    url,
    handleCodeInApp: true,
  });
  window.localStorage.setItem(EMAIL_KEY, email);
}

export function isEmailLinkCallback(href = window.location.href) {
  return isSignInWithEmailLink(auth, href);
}

export function getStoredEmailForSignIn() {
  return window.localStorage.getItem(EMAIL_KEY) || "";
}

export async function completeEmailLinkSignIn(email, href = window.location.href) {
  const result = await signInWithEmailLink(auth, email, href);
  window.localStorage.removeItem(EMAIL_KEY);
  return result.user;
}

// ---- Phone (invisible reCAPTCHA + OTP) ----
let _recaptcha = null;
export function ensureRecaptcha(containerId = "recaptcha-container") {
  if (_recaptcha) return _recaptcha;
  _recaptcha = new RecaptchaVerifier(auth, containerId, {
    size: "invisible",
  });
  return _recaptcha;
}

export function clearRecaptcha() {
  try {
    if (_recaptcha) _recaptcha.clear();
  } catch {}
  _recaptcha = null;
  const el = document.getElementById("recaptcha-container");
  if (el) el.innerHTML = "";
}

export async function startPhoneSignIn(phoneNumberE164, containerId = "recaptcha-container") {
  const verifier = ensureRecaptcha(containerId);
  const confirmation = await signInWithPhoneNumber(auth, phoneNumberE164, verifier);
  return confirmation; // caller stores this and later calls .confirm(code)
}

// ---- Sign out & token listener ----
export async function signOutAll() {
  await fbSignOut(auth);
}

export function onFirebaseUser(callback) {
  return onIdTokenChanged(auth, callback);
}
